//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Moment } from 'moment-timezone';

import * as universalScheduledPostsApi from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApi';
import { ISearchScheduledPostsRequest } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import { isScheduledPin } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiUtils';
import { UniversalPostingGroupName } from '@apis/universal-posts/universalPostsApiModel';
import DateString from '@models/DateString';
import PinterestBoardId from '@models/PinterestBoardId';
import PinterestBoardSectionId from '@models/PinterestBoardSectionId';
import PostableId from '@models/PostableId';
import PostableStatus from '@models/PostableStatus';
import TimeZoneId from '@models/TimeZoneId';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { Normalized } from '@utils/normalize';

import {
  IPinterestScheduleState,
  NormalizedScheduledPins,
  SCHEDULE_PIN_FETCH_LIMIT,
} from 'app/pinterest/redux/schedule/pinterestScheduleModel';
import {
  getPinFetchStartAndEndDate,
  isStartDateOutsideOfLastFetchedWindow,
  mergeScheduledPinsLists,
} from 'app/pinterest/redux/schedule/pinterestScheduleUtils';
import { getCurrentStartDate } from 'app/pinterest/redux/schedule/selectors/getCurrentPinterestStartDate';

const initialState: IPinterestScheduleState = {
  scheduledPins: Normalized.empty(),
  startDate: DateString.getToday(TimeZoneId.Utc),
  filterBoardId: undefined,
  filterBoardSectionId: undefined,
  lastFetchedStartDate: DateString.getToday(TimeZoneId.Utc),

  errors: {
    fetchScheduledPinsError: undefined,
  },
  flags: {
    isFetchingScheduledPins: false,
    isInitialScheduleDataLoaded: false,
    isDeletingPin: false,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.PinterestSchedule,
  initialState,
  reducers: {
    setInitialScheduledData(
      state,
      action: PayloadAction<{
        scheduledPins: NormalizedScheduledPins;
        startDate: DateString;
      }>,
    ) {
      state.flags.isInitialScheduleDataLoaded = true;
      state.scheduledPins = action.payload.scheduledPins;
      state.startDate = action.payload.startDate;
      state.lastFetchedStartDate = action.payload.startDate;
    },
    setStartDate(state, action: PayloadAction<DateString>) {
      state.startDate = action.payload;
    },
    setLastFetchedStartDate(state, action: PayloadAction<DateString>) {
      state.lastFetchedStartDate = action.payload;
    },
    setFilterBoardId(
      state,
      action: PayloadAction<{
        boardId?: PinterestBoardId;
        boardSectionId?: PinterestBoardSectionId;
      }>,
    ) {
      state.filterBoardId = action.payload.boardId;
      state.filterBoardSectionId = action.payload.boardSectionId;
    },
    requestScheduledPosts(state) {
      state.errors.fetchScheduledPinsError = undefined;
      state.flags.isFetchingScheduledPins = true;
    },
    receiveScheduledPostsSucceeded(
      state,
      action: PayloadAction<NormalizedScheduledPins>,
    ) {
      state.flags.isFetchingScheduledPins = false;
      state.scheduledPins = action.payload;
    },
    receiveScheduledPostsFailed(state) {
      state.flags.isFetchingScheduledPins = false;
    },
    requestDeleteScheduledPin(state) {
      state.flags.isDeletingPin = true;
    },
    deleteScheduledPinSucceeded(state) {
      state.flags.isDeletingPin = false;
    },
    deleteScheduledPinFailed(state) {
      state.flags.isDeletingPin = false;
    },
  },
});

export const { setFilterBoardId } = actions;

export const loadInitialScheduleData = createThunk(
  () => async (dispatch, getState) => {
    const tz = getUserTimeZone(getState());
    const today = DateString.getToday(tz);

    try {
      const scheduledPins = await dispatch(getAndParseAllScheduledPins(today));
      dispatch(
        actions.setInitialScheduledData({ scheduledPins, startDate: today }),
      );
    } catch (_e) {
      dispatch(
        actions.setInitialScheduledData({
          scheduledPins: Normalized.empty(),
          startDate: today,
        }),
      );
    }
  },
);

export const refreshScheduleData = createThunk(
  () => async (dispatch, getState) => {
    const { isFetchingScheduledPins } = getState().pinterest.schedule.flags;
    const startDate = getCurrentStartDate(getState());

    if (isFetchingScheduledPins) {
      return;
    }

    dispatch(actions.requestScheduledPosts());

    try {
      const scheduledPosts = await dispatch(
        getAndParseAllScheduledPins(startDate),
      );
      dispatch(actions.receiveScheduledPostsSucceeded(scheduledPosts));
    } catch (_e) {
      dispatch(actions.receiveScheduledPostsFailed());
    }
  },
);

export const setStartDateAndFetchPosts = createThunk(
  (newStartDate: DateString) => async (dispatch, getState) => {
    const { lastFetchedStartDate } = getState().pinterest.schedule;

    dispatch(actions.setStartDate(newStartDate));

    const shouldFetchMorePosts = isStartDateOutsideOfLastFetchedWindow(
      newStartDate,
      lastFetchedStartDate,
    );

    if (shouldFetchMorePosts) {
      dispatch(actions.requestScheduledPosts());
      dispatch(actions.setLastFetchedStartDate(newStartDate));

      try {
        const currentPins = getState().pinterest.schedule.scheduledPins;
        const newPins = await dispatch(
          getAndParseAllScheduledPins(newStartDate),
        );
        const combinedPins = mergeScheduledPinsLists([currentPins, newPins]);
        dispatch(actions.receiveScheduledPostsSucceeded(combinedPins));
      } catch (_e) {
        dispatch(actions.receiveScheduledPostsFailed());
      }
    }
  },
);

const getAndParseScheduledPinSet = async (
  start: Moment,
  end?: Moment,
  offset = 0,
) => {
  const requestParams: ISearchScheduledPostsRequest = {
    after: start.toDate(),
    before: end?.toDate(),
    groups: [UniversalPostingGroupName.PinterestOrganicPinGroup],
    limit: SCHEDULE_PIN_FETCH_LIMIT,
    offset,
    status: [
      PostableStatus.Dispatched,
      PostableStatus.Failed,
      PostableStatus.Pending,
      PostableStatus.Posted,
      PostableStatus.Processing,
      PostableStatus.Scheduled,
    ],
  };

  const { data: scheduledPosts } =
    await universalScheduledPostsApi.searchScheduledPosts({
      ...requestParams,
    });

  const scheduledPins = scheduledPosts.filter(isScheduledPin);

  const isEndReached = scheduledPins.length < SCHEDULE_PIN_FETCH_LIMIT;

  return {
    isEndReached,
    scheduledPins,
  };
};

export const deleteScheduledPin = createThunk(
  (postId: PostableId) =>
    async (dispatch): Promise<boolean> => {
      dispatch(actions.requestDeleteScheduledPin());

      try {
        await universalScheduledPostsApi.deleteScheduledPosts([postId]);

        dispatch(actions.deleteScheduledPinSucceeded());

        return true;
      } catch (e) {
        dispatch(actions.deleteScheduledPinFailed());
        return false;
      }
    },
);

const getAndParseAllScheduledPins = createThunk(
  (startDate: DateString) => async (_, getState) => {
    const tz = getUserTimeZone(getState());
    const { defaultEndDate, defaultStartDate } =
      getPinFetchStartAndEndDate(startDate);
    const start = DateString.toMoment(defaultStartDate, tz);
    const end = DateString.toMoment(defaultEndDate, tz);

    let { isEndReached, scheduledPins } = await getAndParseScheduledPinSet(
      start,
      end,
    );

    while (!isEndReached) {
      const nextResponse = await getAndParseScheduledPinSet(
        start,
        end,
        scheduledPins.length,
      );
      isEndReached = nextResponse.isEndReached;

      scheduledPins = [...scheduledPins, ...nextResponse.scheduledPins];
    }

    return Normalized.from(scheduledPins, (pin) => pin.id);
  },
);

const changeStartDateByOffset = createThunk(
  (offset: number) => (dispatch, getState) => {
    const currentStartDate = getCurrentStartDate(getState());
    const newStartDate = DateString.addDays(currentStartDate, offset);
    void dispatch(setStartDateAndFetchPosts(newStartDate));
  },
);

export const navigateCalendarForward = createThunk(() => (dispatch) => {
  dispatch(changeStartDateByOffset(1));
});

export const navigateCalendarBackward = createThunk(() => (dispatch) => {
  dispatch(changeStartDateByOffset(-1));
});

export const navigateCalendarToDate = createThunk(
  (date: DateString) => (dispatch) => {
    void dispatch(setStartDateAndFetchPosts(date));
  },
);

export const navigateCalendarToToday = createThunk(
  () => (dispatch, getState) => {
    const tz = getUserTimeZone(getState());
    const today = DateString.getToday(tz);
    dispatch(navigateCalendarToDate(today));
  },
);

export default reducer;
